/**
 * PasteConfigBar — shows the copied config and pastes it onto the selected rows.
 * Run-state (auto-run / pause) is never touched; no weights are pushed live.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef, useState, type FC } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { trackerLabel } from '@trafficpulse/shared';

import { Button } from '@/components/ui';

import type { CopiedOptimizerConfig, PasteFieldKey } from '../optimizer-clipboard.types';
import { DuplicateToTargetDialog } from './DuplicateToTargetDialog';
import { PasteConfirmDialog } from './PasteConfirmDialog';

export interface PasteConfigBarProps {
  copied: CopiedOptimizerConfig;
  selectedCount: number;
  isPasting: boolean;
  onPaste: (fields: Set<PasteFieldKey>) => void;
  onClearSelection: () => void;
  onClearCopied: () => void;
}

export const PasteConfigBar: FC<PasteConfigBarProps> = ({
  copied,
  selectedCount,
  isPasting,
  onPaste,
  onClearSelection,
  onClearCopied,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const { source, recipe } = copied;
  const providerLabel = trackerLabel(source.provider);
  const groups = recipe.formula.groups.length;

  // Keep the dialog open with a spinner while the paste runs; close once a
  // paste we started has settled (success or failure — the toast reports it).
  const pasteStarted = useRef(false);
  useEffect(() => {
    if (isPasting) pasteStarted.current = true;
    else if (pasteStarted.current) {
      pasteStarted.current = false;
      setConfirming(false);
    }
  }, [isPasting]);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
      <ClipboardCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="min-w-0">
        Copied from <span className="font-medium">{source.campaignName}</span>{' '}
        <span className="text-muted-foreground">
          ({providerLabel} · {recipe.mode} · {groups} formula group{groups === 1 ? '' : 's'})
        </span>
      </span>
      <div className="ml-auto flex items-center gap-2">
        {selectedCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onClearSelection}>
            Clear selection
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onClearCopied}>
          Clear copied
        </Button>
        <Button variant="outline" size="sm" onClick={() => setDuplicating(true)}>
          Paste to a campaign…
        </Button>
        <Button size="sm" disabled={selectedCount === 0 || isPasting} onClick={() => setConfirming(true)}>
          Paste to {selectedCount} selected
        </Button>
      </div>
      {confirming && (
        <PasteConfirmDialog
          copied={copied}
          targetCount={selectedCount}
          isBusy={isPasting}
          onConfirm={onPaste}
          onCancel={() => setConfirming(false)}
        />
      )}
      {duplicating && (
        <DuplicateToTargetDialog copied={copied} onClose={() => setDuplicating(false)} />
      )}
    </div>
  );
};
